'use client';

import { MaskitoOptions } from '@maskito/core';
import { useCallback, useMemo, useState } from 'react';

import {
  COUNTRY_CODE_US,
  CountryCode,
} from '@sola/schema/dotcom/shared/geo.js';

// (___) ___-____
export const USPhoneNumberMask = [
  '(',
  /\d/,
  /\d/,
  /\d/,
  ')',
  ' ',
  /\d/,
  /\d/,
  /\d/,
  '-',
  /\d/,
  /\d/,
  /\d/,
  /\d/,
];

// 27703
export const postalCodeMask = new Array(5).fill(/\d/);
export const postalCodeMaskUS = [
  /\d/,
  /\d/,
  /\d/,
  /\d/,
  /\d/,
  '-',
  /\d/,
  /\d/,
  /\d/,
  /\d/,
];
export const postalCodeMaskCanada = [
  /[a-zA-Z]/,
  /\d/,
  /[a-zA-Z]/,
  ' ',
  /\d/,
  /[a-zA-Z]/,
  /\d/,
];

const dateMasks = {
  enAndEs: [/\d/, /\d/, '/', /\d/, /\d/, '/', /\d/, /\d/, /\d/, /\d/],
  ko: [/\d/, /\d/, /\d/, /\d/, '.', /\d/, /\d/, '.', /\d/, /\d/],
};

export type GeneratePhoneOrPostalMaskFunc = (countryCode: CountryCode) => {
  options?: MaskitoOptions;
  placeholder?: string;
};

/**
 * Hook for generating masks, placeholders, and validations based on country ISO code.
 */
export const usePhoneMask = (countryCode: CountryCode) => {
  const [generateMask, setGenerateMask] =
    useState<GeneratePhoneOrPostalMaskFunc | null>(null);

  /**
   * Function to lazy load the mask assets.
   * This will only be called once, and serves to load masking dependencies that are needed
   * for the generateMask function, which can then be subsequently used to dynamically generate
   * a masks, placeholders, and validations based on country code.
   *
   * @TODO Don't forget to check how these imports are cached to make sure browser isn't downloading a unique bundle every time.
   *
   */
  const loadMask = useCallback(async () => {
    // NOTE Eventually we will probably need these dependencies for more mature functionality and validation.
    // const [maskitoPhoneGenerator, metadata, getCountryCallingCode] =
    //   await Promise.all([
    //     import('@maskito/phone').then(
    //       (mod) => mod.maskitoPhoneOptionsGenerator,
    //     ),
    //     import('libphonenumber-js/min/metadata').then((mod) => mod.default),
    //     import('libphonenumber-js/core').then(
    //       (mod) => mod.getCountryCallingCode,
    //     ),
    //   ]);

    const generateMaskFunc = (countryCodeToMask: CountryCode) => {
      // const countryCallingCode = getCountryCallingCode(
      //   countryCodeToMask,
      //   metadata,
      // );
      return countryCodeToMask === COUNTRY_CODE_US
        ? {
            options: {
              mask: USPhoneNumberMask,
            },
            placeholder: '(___) ___-____',
          }
        : // Currently replicating legacy. Can definitely be fleshed out.
          {
            options: {
              mask: /^[0-9]*$/,
            },
          };
    };

    setGenerateMask(() => generateMaskFunc);
  }, []);

  const mask = useMemo(() => {
    if (!generateMask) {
      return;
    }
    return generateMask(countryCode);
  }, [countryCode, generateMask]);

  return {
    loadMask,
    mask,
  };
};

export const usePostalMask = (countryCode: CountryCode) => {
  const [generateMask, setGenerateMask] =
    useState<GeneratePhoneOrPostalMaskFunc | null>(null);

  /**
   * Function to lazy load the mask assets.
   * This will only be called once, and serves to load masking dependencies that are needed
   * for the generateMask function, which can then be subsequently used to dynamically generate
   * a masks, placeholders, and validations based on country code.
   *
   * @TODO Don't forget to check how these imports are cached to make sure browser isn't downloading a unique bundle every time.
   *
   */
  const loadMask = useCallback(async () => {
    const generateMaskFunc = (countryCodeToMask: CountryCode) => {
      switch (countryCodeToMask) {
        case 'US':
          return {
            options: {
              mask: postalCodeMaskUS,
            },
          };
        case 'MX':
          return {
            options: {
              mask: postalCodeMask,
            },
          };
        case 'CA':
          return {
            options: {
              mask: postalCodeMaskCanada,
            },
          };
        default:
          return {};
      }
    };

    setGenerateMask(() => generateMaskFunc);
  }, []);

  /**
   * Generate a mask anytime the country code changes.
   */
  const mask = useMemo(() => {
    if (!generateMask) {
      return;
    }
    return generateMask(countryCode);
  }, [countryCode, generateMask]);

  return {
    loadMask,
    mask,
  };
};

type GenerateDateMaskFunc = (localeCode: string) => {
  options?: MaskitoOptions;
  placeholder?: string;
};

export const useDateMask = (localeCode: string) => {
  const [generateMask, setGenerateMask] = useState<GenerateDateMaskFunc | null>(
    null,
  );

  /**
   * Function to lazy load the mask assets.
   * This will only be called once, and serves to load masking dependencies that are needed
   * for the generateMask function, which can then be subsequently used to dynamically generate
   * a masks, placeholders, and validations based on country code.
   *
   * @TODO Don't forget to check how these imports are cached to make sure browser isn't downloading a unique bundle every time.
   *
   */
  const loadMask = useCallback(async () => {
    const generateMaskFunc = (localeCodeToMask: string) => {
      switch (localeCodeToMask) {
        case 'ko':
          return {
            options: {
              mask: dateMasks.ko,
            },
            placeholder: 'yyyy.mm.dd',
          };
        case 'es':
          return {
            options: {
              mask: dateMasks.enAndEs,
            },
            placeholder: 'dd/mm/yyyy',
          };
        default:
          return {
            options: {
              mask: dateMasks.enAndEs,
            },
            placeholder: 'mm/dd/yyyy',
          };
      }
    };

    setGenerateMask(() => generateMaskFunc);
  }, []);

  /**
   * Generate a mask anytime the country code changes.
   */
  const mask = useMemo(() => {
    if (!generateMask) {
      return;
    }
    return generateMask(localeCode);
  }, [localeCode, generateMask]);

  return {
    loadMask,
    mask,
  };
};

// 1234 1234 1234 1234 123
export const CCNumberMask = [
  ...new Array(4).fill(/\d/),
  ' ',
  ...new Array(4).fill(/\d/),
  ' ',
  ...new Array(4).fill(/\d/),
  ' ',
  ...new Array(4).fill(/\d/),
  ' ',
  ...new Array(3).fill(/\d/),
];

// 123
export const CCVMask = [...new Array(3).fill(/\d/)];

// 12/34
export const CCDateMask = [
  ...new Array(2).fill(/\d/),
  '/',
  ...new Array(2).fill(/\d/),
];

// 01637017
export const sponsorDonorMask = new Array(8).fill(/\d/);
